import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import TabNavigation from '../../components/TabNavigation';
import { Building2, MapPin, FileText, TrendingUp, AlertCircle } from 'lucide-react';

const ERSDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const [energyMetrics, setEnergyMetrics] = useState(null);

  useEffect(() => {
    fetchStats();
    fetchEnergyMetrics();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/ers/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch ERS stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEnergyMetrics = async () => {
    try {
      const response = await axios.get(`${API}/ers/data/dashboard/energy-metrics`);
      setEnergyMetrics(response.data);
    } catch (error) {
      console.error('Failed to fetch energy metrics:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Energy & Resource Studies</h1>
          <p className="text-gray-600 mt-2">Profit-Linked Sustainability Intelligence Engine</p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-gray-600 font-semibold mb-1">Total Sites</div>
                <div className="text-3xl font-bold text-green-700">{stats?.total_sites || 0}</div>
              </div>
              <MapPin className="w-12 h-12 text-green-500 opacity-50" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-purple-500">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-gray-600 font-semibold mb-1">Total Studies</div>
                <div className="text-3xl font-bold text-purple-700">{stats?.total_studies || 0}</div>
              </div>
              <FileText className="w-12 h-12 text-purple-500 opacity-50" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-orange-500">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-gray-600 font-semibold mb-1">Active Studies</div>
                <div className="text-3xl font-bold text-orange-700">{stats?.active_studies || 0}</div>
              </div>
              <TrendingUp className="w-12 h-12 text-orange-500 opacity-50" />
            </div>
          </div>
        </div>

        {/* Energy Consumption Metrics */}
        {energyMetrics && (
          <div className="bg-white rounded-xl shadow-md p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Energy & Resources Overview</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 border border-yellow-200 rounded-lg p-4">
                <div className="text-xs text-yellow-700 font-semibold mb-1">Total Electricity</div>
                <div className="text-xl font-bold text-yellow-900">
                  {energyMetrics.total_electricity_kwh?.toLocaleString() || 0} <span className="text-sm">kWh</span>
                </div>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-4">
                <div className="text-xs text-blue-700 font-semibold mb-1">Avg Power Factor</div>
                <div className="text-xl font-bold text-blue-900">
                  {energyMetrics.avg_power_factor || 'N/A'}
                </div>
              </div>
              <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 border border-cyan-200 rounded-lg p-4">
                <div className="text-xs text-cyan-700 font-semibold mb-1">Total Water</div>
                <div className="text-xl font-bold text-cyan-900">
                  {energyMetrics.total_water_m3?.toLocaleString() || 0} <span className="text-sm">m³</span>
                </div>
              </div>
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 rounded-lg p-4">
                <div className="text-xs text-gray-700 font-semibold mb-1">Total Production</div>
                <div className="text-xl font-bold text-gray-900">
                  {energyMetrics.total_production?.toLocaleString() || 0} <span className="text-sm">MT</span>
                </div>
              </div>
            </div>
            {energyMetrics.total_production > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-600 font-semibold mb-2">Intensities (per MT)</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {energyMetrics.total_electricity_kwh > 0 && (
                    <div>
                      <div className="text-xs text-gray-500">Energy</div>
                      <div className="text-lg font-bold text-indigo-900">
                        {(energyMetrics.total_electricity_kwh / energyMetrics.total_production).toFixed(2)} <span className="text-xs text-gray-600">kWh/MT</span>
                      </div>
                    </div>
                  )}
                  {energyMetrics.total_water_m3 > 0 && (
                    <div>
                      <div className="text-xs text-gray-500">Water</div>
                      <div className="text-lg font-bold text-cyan-900">
                        {(energyMetrics.total_water_m3 / energyMetrics.total_production).toFixed(2)} <span className="text-xs text-gray-600">m³/MT</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Study Status Breakdown */}
        {stats?.studies_by_status && (
          <div className="bg-white rounded-xl shadow-md p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Studies by Status</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="text-2xl font-bold text-gray-700">{stats.studies_by_status.Draft}</div>
                <div className="text-sm text-gray-600 mt-1">Draft</div>
              </div>
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <div className="text-2xl font-bold text-blue-700">{stats.studies_by_status.Ongoing}</div>
                <div className="text-sm text-blue-600 mt-1">Ongoing</div>
              </div>
              <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                <div className="text-2xl font-bold text-yellow-700">{stats.studies_by_status.Review}</div>
                <div className="text-sm text-yellow-600 mt-1">Under Review</div>
              </div>
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <div className="text-2xl font-bold text-green-700">{stats.studies_by_status.Final}</div>
                <div className="text-sm text-green-600 mt-1">Final</div>
              </div>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={() => navigate('/ers/sites')}
              className="bg-gradient-to-r from-green-600 to-green-700 text-white font-bold py-4 px-6 rounded-lg hover:shadow-lg transform hover:-translate-y-1 transition-all duration-200"
            >
              <MapPin className="w-6 h-6 inline-block mr-2" />
              Manage Sites
            </button>
            <button
              onClick={() => navigate('/ers/studies')}
              className="bg-gradient-to-r from-purple-600 to-purple-700 text-white font-bold py-4 px-6 rounded-lg hover:shadow-lg transform hover:-translate-y-1 transition-all duration-200"
            >
              <FileText className="w-6 h-6 inline-block mr-2" />
              Manage Studies
            </button>
          </div>
        </div>

        {/* Info Banner */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-start">
            <AlertCircle className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Getting Started</h3>
              <p className="text-blue-800 text-sm">
                Start by adding sites for your organization, then create ERS studies for specific sites. 
                Each study will guide you through desktop assessment, on-site verification, baseline calculations, and opportunity identification.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ERSDashboard;